import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Button, Card, CardDescription, CardHeader, CardTitle } from '@monigo/ui';
import { ID_DOCUMENT_TYPES, type IdDocumentType } from '@monigo/shared';
import { useSession } from '../../lib/store/session.js';
import { supabase } from '../../lib/supabase.js';
import { captureCamera } from './camera.js';
import { useSubmitKyc } from './api.js';

export function KycCapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSession();
  const submit = useSubmitKyc();

  const [docType, setDocType] = useState<IdDocumentType>('passport');
  const [docTypeOpen, setDocTypeOpen] = useState(false);

  const [selfie, setSelfie] = useState<{ blob: Blob; url: string } | null>(null);
  const [idDoc, setIdDoc] = useState<{ blob: Blob; url: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [previewMode, setPreviewMode] = useState<'selfie' | 'id' | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (previewMode && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [previewMode]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) track.stop();
        streamRef.current = null;
      }
    };
  }, []);

  function closePreview() {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    setPreviewMode(null);
  }

  async function startPreview(which: 'selfie' | 'id') {
    setErr(null);
    closePreview();
    const facing = which === 'selfie' ? 'user' : 'environment';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1080 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      setPreviewMode(which);
    } catch {
      setErr(t('kyc.noCamera'));
    }
  }

  async function captureFromPreview() {
    if (!videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx && previewMode === 'selfie') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', 0.85),
    );
    if (!blob) {
      setErr(t('errors.generic'));
      closePreview();
      return;
    }

    const url = URL.createObjectURL(blob);
    const which = previewMode as 'selfie' | 'id';

    if (which === 'selfie' && selfie) URL.revokeObjectURL(selfie.url);
    if (which === 'id' && idDoc) URL.revokeObjectURL(idDoc.url);

    if (which === 'selfie') setSelfie({ blob, url });
    else setIdDoc({ blob, url });

    closePreview();
  }

  async function handleShoot(which: 'selfie' | 'id') {
    setErr(null);
    // Native: Capacitor opens its own camera UI with a built-in preview.
    if (Capacitor.isNativePlatform()) {
      try {
        const blob = await captureCamera(which === 'selfie' ? 'user' : 'environment');
        const url = URL.createObjectURL(blob);
        if (which === 'selfie' && selfie) URL.revokeObjectURL(selfie.url);
        if (which === 'id' && idDoc) URL.revokeObjectURL(idDoc.url);
        if (which === 'selfie') setSelfie({ blob, url });
        else setIdDoc({ blob, url });
      } catch {
        setErr(t('kyc.noCamera'));
      }
      return;
    }
    // Web: show our own live preview before snapping.
    await startPreview(which);
  }

  async function onSubmit() {
    if (!user || !selfie || !idDoc) return;
    const submissionId = crypto.randomUUID();
    const selfieKey = `${user.id}/${submissionId}/selfie.jpg`;
    const idKey = `${user.id}/${submissionId}/id.jpg`;
    const a = await supabase.storage.from('kyc-documents').upload(selfieKey, selfie.blob, {
      contentType: 'image/jpeg',
      upsert: false,
    });
    const b = await supabase.storage.from('kyc-documents').upload(idKey, idDoc.blob, {
      contentType: 'image/jpeg',
      upsert: false,
    });
    if (a.error || b.error) {
      setErr(t('errors.generic'));
      return;
    }
    await submit.mutateAsync({
      id_document_type: docType,
      selfie_storage_key: selfieKey,
      id_document_storage_key: idKey,
    });
    navigate('/kyc/pending');
  }

  if (previewMode) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black">
        <div className="relative flex-1 overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`h-full w-full object-cover ${previewMode === 'selfie' ? '-scale-x-100' : ''}`}
          />
          <ScanOverlay />
        </div>
        <div className="flex items-center justify-center gap-6 bg-black/80 px-4 py-6">
          <Button variant="secondary" onClick={closePreview}>
            {t('common.cancel')}
          </Button>
          <Button onClick={captureFromPreview}>{t('kyc.capture')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('kyc.captureTitle')}</CardTitle>
          <CardDescription>{t('kyc.noGalleryNote')}</CardDescription>
        </CardHeader>
        <div className="flex flex-col gap-5 px-5 pb-5">
          <div className="relative">
            <label className="mb-1 block text-sm font-semibold text-text-muted">
              {t('kyc.selectIdType')}
            </label>
            <button
              type="button"
              onClick={() => setDocTypeOpen(!docTypeOpen)}
              className="flex h-12 w-full items-center justify-between rounded-xl border border-border bg-surface px-4 text-left text-text"
            >
              <span>{t(`kyc.${docType}`)}</span>
              <svg
                className={`h-4 w-4 text-text-muted transition-transform ${docTypeOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {docTypeOpen ? (
              <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-surface shadow-lg">
                {ID_DOCUMENT_TYPES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      setDocType(d);
                      setDocTypeOpen(false);
                    }}
                    className={`flex w-full px-4 py-3 text-left text-sm transition hover:bg-surface-elevated ${
                      d === docType ? 'font-semibold text-primary' : 'text-text'
                    }`}
                  >
                    {t(`kyc.${d}`)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <CaptureBox
            label={t('kyc.captureSelfie')}
            captured={selfie}
            onShoot={() => handleShoot('selfie')}
            onRetake={() => setSelfie(null)}
          />
          <CaptureBox
            label={t('kyc.captureId')}
            captured={idDoc}
            onShoot={() => handleShoot('id')}
            onRetake={() => setIdDoc(null)}
          />

          {err ? <p className="text-sm text-danger">{err}</p> : null}
          <Button
            onClick={onSubmit}
            isLoading={submit.isPending}
            disabled={!selfie || !idDoc}
            className="w-full"
          >
            {t('kyc.submitCta')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ScanOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute inset-6 rounded-3xl border-2 border-success/70 shadow-[0_0_40px_hsl(var(--color-success)/0.4)]" />
      <div className="absolute inset-x-6 top-6 h-[calc(100%-3rem)] overflow-hidden rounded-3xl">
        <div className="animate-scan-sweep absolute inset-x-0 h-1 bg-success shadow-[0_0_18px_4px_hsl(var(--color-success)/0.8)]" />
      </div>
    </div>
  );
}

function CaptureBox({
  label,
  captured,
  onShoot,
  onRetake,
}: {
  label: string;
  captured: { url: string } | null;
  onShoot: () => void;
  onRetake: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-4">
      <div className="mb-3 text-sm font-semibold text-text">{label}</div>
      {captured ? (
        <div className="flex items-center gap-4">
          <img src={captured.url} alt="" className="h-28 w-28 rounded-lg object-cover shadow-sm" />
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-success">✓ {t('kyc.captured')}</span>
            <Button variant="secondary" size="sm" onClick={onRetake}>
              {t('kyc.retake')}
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={onShoot} variant="secondary" className="w-full gap-2">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {label}
        </Button>
      )}
    </div>
  );
}
